import React from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { Seo } from "@/components/Seo";
import { SectionHeading } from "@/components/SectionHeading";
import { CTASection } from "@/components/CTASection";
import { SERVICES } from "@/data/site";

const PageHero = ({ overline, title, subtitle, image }) => (
  <section className="relative pt-40 pb-24 bg-navy overflow-hidden">
    {image && (
      <div className="absolute inset-0">
        <img src={image} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy/80" />
      </div>
    )}
    <div className="relative max-w-7xl mx-auto px-5">
      <p className="text-xs sm:text-sm tracking-[0.25em] uppercase font-bold text-gold mb-5">{overline}</p>
      <h1 className="font-display text-5xl sm:text-6xl text-white leading-tight max-w-3xl">{title}</h1>
      {subtitle && <p className="mt-6 text-white/70 text-lg max-w-2xl">{subtitle}</p>}
    </div>
  </section>
);

export default function Services() {
  return (
    <PublicLayout>
      <Seo title="Our Services" description="Residential & commercial construction, concrete & wooden homes, waterfront structures, docks, piers, seawalls, retaining walls and land development across Belize." />
      <PageHero
        overline="Our Services"
        title="Full-scope construction, engineered to last."
        subtitle="A single, dependable partner for residential, commercial, waterfront and land development projects across Belize."
        image="https://images.unsplash.com/photo-1599707254554-027aeb4deacd?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
      />

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 grid md:grid-cols-2 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Hammer;
            return (
              <motion.div
                key={s.key}
                data-testid={`service-${s.key}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 2) * 0.08 }}
                className="flex gap-6 bg-white border border-gray-100 p-8 hover:border-gold hover:shadow-lg transition-all duration-300 group"
              >
                <div className="h-14 w-14 shrink-0 flex items-center justify-center bg-navy text-gold group-hover:bg-gold group-hover:text-navy transition-colors">
                  <Icon size={24} />
                </div>
                <div>
                  <h3 className="font-display text-2xl text-navy">{s.title}</h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-secondary">
        <div className="max-w-7xl mx-auto px-5">
          <SectionHeading overline="How We Work" title="A clear path from vision to handover." center />
          <div className="mt-14 grid md:grid-cols-4 gap-8">
            {[
              { n: "01", t: "Consultation", d: "We listen to your vision, site and budget." },
              { n: "02", t: "Design & Quote", d: "Transparent planning and detailed estimates." },
              { n: "03", t: "Construction", d: "Skilled craftsmanship with strict quality control." },
              { n: "04", t: "Handover", d: "Delivered on time, on budget, ready to enjoy." },
            ].map((p, i) => (
              <motion.div key={p.n} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="relative">
                <div className="font-display text-6xl text-gold/30">{p.n}</div>
                <h3 className="font-display text-xl text-navy mt-2">{p.t}</h3>
                <p className="mt-2 text-muted-foreground text-sm">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </PublicLayout>
  );
}
